import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Offcanvas } from 'react-bootstrap';
import { AuthContext } from '../App';
import Footer from './Footer';
import envConfig from '../envConfig';
import Swal from 'sweetalert2';
import DataTable from 'react-data-table-component';
import * as XLSX from 'xlsx'; // Import xlsx library for Excel export
import { saveAs } from 'file-saver'; // For downloading the file
import { ArrowClockwise } from 'react-bootstrap-icons';

function UserManagement(props) {
    const { isAuthenticated, logout } = useContext(AuthContext);
    const navigate = useNavigate();
    const [show, setShow] = useState(false);
    const [countries, setCountries] = useState(null);
    const [roles, setRoles] = useState([]);
    const [users, setUsers] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedRole, setSelectedRole] = useState('');
    const [formError, setFormError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [pageNo, setPageNo] = useState(1);
    const [perPage, setPerPage] = useState(5);
    const [totalRows, setTotalRows] = useState(0);
    const [selectedRows, setSelectedRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [offcanvasCountry, setOffcanvasCountry] = useState('');
    const [offcanvasProvince, setOffcanvasProvince] = useState('');
    const [offcanvasDistrict, setOffcanvasDistrict] = useState('');
    const [offcanvasVillage, setOffcanvasVillage] = useState('');
    const [provinces, setProvinces] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [villages, setVillages] = useState([]);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [roleId, setRoleId] = useState('');
    const [offcanvasFormError, setOffcanvasFormError] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [activeTab, setActiveTab] = useState(null);
    const userDetails = JSON.parse(localStorage.getItem('userDetails')) || {};
    const isSuperAdmin = +userDetails?.role_id === 1;

    useEffect(() => {
        if (selectedCountry !== null) {
            setOffcanvasCountry(selectedCountry);
            handleFilterSubmit();
        }
    }, [selectedCountry]);

    useEffect(() => {
        if (countries) {
            if (!isSuperAdmin) {
                setSelectedCountry(userDetails.allowed_country_id || '');
            } else {
                setSelectedCountry('');
            }
            handleFilterSubmit();
        }
    }, [countries]);

    const handleClose = () => {
        setShow(false);
        // Reset Offcanvas form
        // setOffcanvasCountry('');
        setFirstName('');
        setLastName('');
        setEmail('');
        setPhone('');
        setRoleId('');
        setOffcanvasFormError(null);
        setIsEditMode(false);
        setSelectedUserId(null);
        setOffcanvasProvince('');
        setOffcanvasDistrict('');
        setOffcanvasVillage('');
        setProvinces([]);
        setDistricts([]);
        setVillages([]);
    };

    const handleShow = () => setShow(true);

    const reset = () => {
        setSearchTerm('');
        setPageNo(1);
        setSelectedRows([]);
        setSelectedRole('');
        // if (!selectedCountry) {
        //     setFormError('Please select a country before searching.');
        //     return;
        // }
        fetchUsers('');
    };

    const handleEdit = (user) => {
        setIsEditMode(true);
        setSelectedUserId(user.user_id);
        setFirstName(user.first_name);
        setLastName(user.last_name);
        setEmail(user.email);
        setPhone(user.phone);
        setRoleId(user.role_id);
        setOffcanvasCountry(user.allowed_country_id || '');
        setOffcanvasProvince(user.allowed_adm1_id || '');
        setOffcanvasDistrict(user.allowed_adm2_id || '');
        setOffcanvasVillage(user.allowed_adm3_id || '');
        setShow(true);
    };

    const handleDelete = async (user) => {
        const fullName = `${user.first_name}${user.last_name ? ' ' + user.last_name : ''}`;
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `Do you want to delete ${fullName}? This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
        });

        if (result.isConfirmed) {
            const payload = {
                user_id: user.user_id,
            };
            setIsSubmitting(true);
            try {
                const response = await fetch(`${envConfig.API_URL}/auth/user_mgmt`, {
                    method: 'DELETE',
                    headers: getAuthHeaders(),
                    body: JSON.stringify(payload),
                });
                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`);
                }

                await Swal.fire({
                    title: 'Deleted!',
                    text: `${fullName} has been deleted.`,
                    icon: 'success',
                    confirmButtonText: 'OK',
                });

                const newTotalRows = totalRows - 1;
                const recordsOnCurrentPage = newTotalRows - (pageNo - 1) * perPage;

                if (recordsOnCurrentPage <= 0 && pageNo > 1) {
                    setPageNo(pageNo - 1);
                } else if (newTotalRows === 0) {
                    setPageNo(1);
                }

                setTotalRows(newTotalRows);
                await fetchUsers();
            } catch (error) {
                setFormError(handleApiError(error, error.response));
                Swal.fire({
                    title: 'Error!',
                    text: formError || 'Failed to delete the user.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const handleActivate = async (user) => {
        const fullName = `${user.first_name}${user.last_name ? ' ' + user.last_name : ''}`;
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `Do you want to activate ${fullName}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, activate!',
            cancelButtonText: 'Cancel',
        });

        if (result.isConfirmed) {
            const payload = {
                user_id: user.user_id,
                purpose: 'reactivate',
            };
            setIsSubmitting(true);
            try {
                const response = await fetch(`${envConfig.API_URL}/auth/user_mgmt`, {
                    method: 'PATCH',
                    headers: getAuthHeaders(),
                    body: JSON.stringify(payload),
                });
                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`);
                }

                await Swal.fire({
                    title: 'Activated!',
                    text: `${fullName} has been activated.`,
                    icon: 'success',
                    confirmButtonText: 'OK',
                });

                const newTotalRows = totalRows - 1;
                const recordsOnCurrentPage = newTotalRows - (pageNo - 1) * perPage;

                if (recordsOnCurrentPage <= 0 && pageNo > 1) {
                    setPageNo(pageNo - 1);
                } else if (newTotalRows === 0) {
                    setPageNo(1);
                }

                setTotalRows(newTotalRows);
                await fetchUsers();
            } catch (error) {
                setFormError(handleApiError(error, error.response));
                Swal.fire({
                    title: 'Error!',
                    text: formError || 'Failed to activate the user.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const handleReset = async (user) => {
        const currentYear = new Date().getFullYear();
        const defaultPassword = `bLuEResiLience@${currentYear}`;
        const fullName = `${user.first_name}${user.last_name ? ' ' + user.last_name : ''}`;
        const result = await Swal.fire({
            title: 'Are you sure?',
            html: `The password for ${fullName} will be reset to <br><strong>${defaultPassword}</strong>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, reset!',
            cancelButtonText: 'Cancel',
        });

        if (result.isConfirmed) {
            const payload = {
                purpose: 'reset_password',
                user_id: user.user_id
            };
            setIsSubmitting(true);
            try {
                const response = await fetch(`${envConfig.API_URL}/auth/user_mgmt`, {
                    method: 'PATCH',
                    headers: getAuthHeaders(),
                    body: JSON.stringify(payload),
                });
                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`);
                }

                await Swal.fire({
                    title: 'Password Reset!',
                    html: `Password for ${fullName} has been reset.<br>New Password: <strong>${defaultPassword}</strong>`,
                    icon: 'success',
                    confirmButtonText: 'OK',
                });
            } catch (error) {
                setFormError(handleApiError(error, error.response));
                Swal.fire({
                    title: 'Error!',
                    text: formError || 'Failed to reset password.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const handlePhoneChange = (e) => {
        const value = e.target.value;
        if (/^\d{0,16}$/.test(value)) {
            setPhone(value);
            setOffcanvasFormError(null);
        } else {
            setOffcanvasFormError('Phone must be a maximum of 16 digits with no special characters.');
        }
    };

    const handleEmailChange = (e) => {
        const value = e.target.value.trim();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        setEmail(value);

        if (value === '') {
            setOffcanvasFormError('Email is required.');
        } else if (!emailRegex.test(value)) {
            setOffcanvasFormError('Please enter a valid email address.');
        } else {
            setOffcanvasFormError(null);
        }
    };

    useEffect(() => {
        if (!isAuthenticated) {
            window.location.href = '/login';
        }
    }, [isAuthenticated, navigate]);

    const getAuthHeaders = () => {
        const token = localStorage.getItem('profishBlue_authToken');
        const expiryTime = localStorage.getItem('profishBlue_tokenExpiry');

        const isTokenExpired = !token || !expiryTime || Date.now() > parseInt(expiryTime, 10);

        if (isTokenExpired) {
            logout();
            window.location.href = '/login';
            return {};
        }

        return {
            'Content-Type': 'application/json',
            Authorization: token,
        };
    };

    const handleApiError = (error, response) => {
        if (
            response &&
            response.status === 401 &&
            error.message.includes('Your authentication token is invalid')
        ) {
            setTimeout(() => {
                logout();
                window.location.href = '/login';
            }, 0);
            return 'Session expired. Please log in again.';
        }
        return error.message || 'An error occurred. Please try again.';
    };

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const response = await fetch(`${envConfig.API_URL}/lookups/locations/country`, {
                    headers: getAuthHeaders(),
                });
                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`);
                }
                const data = await response.json();
                setCountries(data.data || []);
            } catch (error) {
                setFormError(handleApiError(error, error.response));
            }
        };

        const fetchRoles = async () => {
            try {
                const response = await fetch(`${envConfig.API_URL}/auth/user_roles`, {
                    headers: getAuthHeaders(),
                });
                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`);
                }
                const data = await response.json();
                setRoles(data.data || []);
            } catch (error) {
                setFormError(handleApiError(error, error.response));
            }
        };

        if (isAuthenticated) {
            fetchCountries();
            fetchRoles();
        }
    }, [isAuthenticated]);

    const fetchUsers = async (term = searchTerm, isExport = false) => {
        setIsLoading(true);
        setFetchError(null);
        if (!activeTab) {
            setActiveTab('active');
        }
        try {
            let url = `${envConfig.API_URL}/auth/user_mgmt`;
            let params = [];
            if (selectedCountry) params.push(`country_id=${selectedCountry}`);
            if (!isExport) {
                params.push(`page=${pageNo}`);
                params.push(`size=${perPage}`);
            }
            params.push(`status=${activeTab ?? 'active'}`);
            if (selectedRole) params.push(`role_id=${selectedRole}`);
            if (term) params.push(`search_for=${encodeURIComponent(term)}`);
            if (params.length > 0) url += '?' + params.join('&');

            const response = await fetch(url, {
                headers: getAuthHeaders(),
            });

            if (!response.ok) {
                throw new Error(`Error: ${response.status}`);
            }

            const data = await response.json();
            if (data.success === 1) {
                const userData = data.data || [];
                if (isExport) {
                    return userData; // Return data for export
                }
                setUsers(userData);
                const newTotalRows = data.metadata?.total_count || userData.length;
                setTotalRows(newTotalRows);
            } else {
                throw new Error('API request failed');
            }
        } catch (error) {
            setFetchError(handleApiError(error, error.response));
            setUsers([]);
            setTotalRows(0);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFilterSubmit = () => {
        setFormError(null);
        setPageNo(1);
        fetchUsers();
    };

    const handleSearchSubmit = (e) => {
        if (e) e.preventDefault();
        setPageNo(1);
        setSelectedRows([]);
        fetchUsers();
    };

    const onCountryChange = (e) => {
        setSelectedCountry(e.target.value);
        // setSearchTerm('');
        // setPageNo(1);
        // setPerPage(5);
        // setSelectedRows([]);
        // setUsers([]);
        // setTotalRows(0);
    };

    const onRoleChange = (e) => {
        setSelectedRole(e.target.value);
    };

    const fetchProvinces = async (country_id) => {
        try {
            const response = await fetch(`${envConfig.API_URL}/lookups/locations/adm1?country_id=${country_id}`, {
                headers: getAuthHeaders(),
            });
            if (!response.ok) {
                throw new Error(`Error: ${response.status}`);
            }
            const data = await response.json();
            setProvinces(data.data || []);
        } catch (error) {
            console.error('Error fetching provinces:', error);
        }
    };

    const fetchDistricts = async (country_id, adm1_id) => {
        try {
            const response = await fetch(`${envConfig.API_URL}/lookups/locations/adm2?country_id=${country_id}&adm1_id=${adm1_id}`, {
                headers: getAuthHeaders(),
            });
            if (!response.ok) {
                throw new Error(`Error: ${response.status}`);
            }
            const data = await response.json();
            setDistricts(data.data || []);
        } catch (error) {
            console.error('Error fetching districts:', error);
        }
    };

    const fetchVillages = async (country_id, adm1_id, adm2_id) => {
        try {
            const response = await fetch(`${envConfig.API_URL}/lookups/locations/adm3?country_id=${country_id}&adm1_id=${adm1_id}&adm2_id=${adm2_id}`, {
                headers: getAuthHeaders(),
            });
            if (!response.ok) {
                throw new Error(`Error: ${response.status}`);
            }
            const data = await response.json();
            setVillages(data.data || []);
        } catch (error) {
            console.error('Error fetching villages:', error);
        }
    };

    useEffect(() => {
        if (offcanvasCountry && (+roleId >= 2)) {
            fetchProvinces(offcanvasCountry);
        } else {
            setProvinces([]);
            setOffcanvasProvince('');
            setDistricts([]);
            setOffcanvasDistrict('');
            setVillages([]);
            setOffcanvasVillage('');
        }
    }, [offcanvasCountry, roleId]);

    useEffect(() => {
        if (offcanvasCountry && offcanvasProvince && (+roleId >= 3)) {
            fetchDistricts(offcanvasCountry, offcanvasProvince);
        } else {
            setDistricts([]);
            setOffcanvasDistrict('');
            setVillages([]);
            setOffcanvasVillage('');
        }
    }, [offcanvasProvince, roleId, offcanvasCountry]);

    useEffect(() => {
        if (offcanvasCountry && offcanvasProvince && offcanvasDistrict && (+roleId >= 5)) {
            fetchVillages(offcanvasCountry, offcanvasProvince, offcanvasDistrict);
        } else {
            setVillages([]);
            setOffcanvasVillage('');
        }
    }, [offcanvasDistrict, roleId, offcanvasCountry, offcanvasProvince]);

    const handleOffcanvasSubmit = async (e) => {
        e.preventDefault();

        setOffcanvasFormError(null);

        // Define required fields by role
        const baseFields = [firstName, lastName, email, phone, roleId];
        let additionalFields = [];
        if (+roleId >= 2) {
            additionalFields.push(offcanvasCountry);
        }
        if (+roleId >= 3) {
            additionalFields.push(offcanvasProvince);
        }
        if (+roleId >= 4) {
            additionalFields.push(offcanvasDistrict);
        }
        if (+roleId >= 5) {
            additionalFields.push(offcanvasVillage);
        }
        const allFields = [...baseFields, ...additionalFields];

        // Check for missing fields
        const hasEmptyField = allFields.some(field => !field);
        if (hasEmptyField) {
            setOffcanvasFormError('All fields are mandatory.');
            return;
        }
        let payload;

        if (isEditMode) {
            payload = {
                allowed_country_id: +roleId >= 2 ? parseInt(offcanvasCountry) : null,
                allowed_adm1_id: +roleId >= 3 ? parseInt(offcanvasProvince) : null,
                allowed_adm2_id: +roleId >= 4 ? parseInt(offcanvasDistrict) : null,
                allowed_adm3_id: +roleId >= 5 ? parseInt(offcanvasVillage) : null,
                status: activeTab,
                purpose: 'update_details',
                first_name: firstName,
                last_name: lastName,
                email,
                phone,
                role_id: +roleId,
                ...(isEditMode && { user_id: selectedUserId })
            };
        } else {
            payload = {
                allowed_country_id: +roleId >= 2 ? parseInt(offcanvasCountry) : null,
                allowed_adm1_id: +roleId >= 3 ? parseInt(offcanvasProvince) : null,
                allowed_adm2_id: +roleId >= 4 ? parseInt(offcanvasDistrict) : null,
                allowed_adm3_id: +roleId >= 5 ? parseInt(offcanvasVillage) : null,
                first_name: firstName,
                last_name: lastName,
                email,
                phone,
                role_id: +roleId,
            };
        }
        setIsSubmitting(true);
        try {
            const response = await fetch(`${envConfig.API_URL}/auth/user_mgmt`, {
                method: isEditMode ? 'PATCH' : 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(payload),
            });

            const result = await response.json();

            if (!response.ok || result.success === 0) {
                throw new Error(result.message || `Error: ${response.status}`);
            }

            await Swal.fire({
                title: isEditMode ? 'Updated!' : 'Saved!',
                text: isEditMode
                    ? `${firstName}${lastName ? ' ' + lastName : ''} has been successfully updated.`
                    : `${firstName}${lastName ? ' ' + lastName : ''} has been added successfully.`,
                icon: 'success',
                confirmButtonText: 'OK',
            });

            await fetchUsers();
            handleClose();
        } catch (error) {
            const message = error.message || 'Failed to submit the form.';
            setOffcanvasFormError(message);
            Swal.fire({
                title: 'Error!',
                text: message,
                icon: 'error',
                confirmButtonText: 'OK',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // New function to handle Excel export
    const handleExport = async () => {
        try {
            setIsLoading(true);
            const exportData = await fetchUsers(searchTerm, true); // Fetch all data without pagination
            if (!exportData || exportData.length === 0) {
                Swal.fire({
                    title: 'No Data',
                    text: 'No users found to export.',
                    icon: 'info',
                    confirmButtonText: 'OK',
                });
                return;
            }

            // Map data to match DataTable columns
            const exportFormattedData = exportData.map((row, index) => ({
                SNo: index + 1,
                Country: row.allowed_country || '--',
                'First Name': row.first_name || '--',
                'Last Name': row.last_name || '--',
                Email: row.email || '--',
                Phone: row.phone || '--',
                Role: row.role || '--',
                Status: row.status || 'Active',
            }));

            // Create worksheet
            const ws = XLSX.utils.json_to_sheet(exportFormattedData);

            // Define column widths for better readability
            ws['!cols'] = [
                { wch: 10 }, // SNo
                { wch: 20 }, // Country
                { wch: 20 }, // First Name
                { wch: 20 }, // Last Name
                { wch: 30 }, // Email
                { wch: 20 }, // Phone
                { wch: 15 }, // Role
                { wch: 15 }, // Status
            ];

            // Add headers styling (optional, requires additional setup)
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Users');

            // Generate Excel file
            const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
            saveAs(data, `Users_${activeTab}_${new Date().toISOString().split('T')[0]}.xlsx`);
        } catch (error) {
            Swal.fire({
                title: 'Error!',
                text: 'Failed to export data.',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const columns = [
        {
            name: 'SNo',
            selector: (row, index) => {
                const sno = index + 1 + (pageNo - 1) * perPage;
                return sno;
            },
            sortable: false,
            width: '60px',
        },
        {
            name: 'Country',
            selector: (row) => row.allowed_country || '--',
            sortable: true,
            width: '100px',
        },
        {
            name: 'First Name',
            selector: (row) => row.first_name || '--',
            sortable: true,
            width: '200px',
        },
        {
            name: 'Last Name',
            selector: (row) => row.last_name || '--',
            sortable: true,
            width: '200px',
        },
        {
            name: 'Email',
            selector: (row) => row.email || '--',
            sortable: true,
            width: '250px',
        },
        {
            name: 'Phone',
            selector: (row) => row.phone || '--',
            sortable: true,
        },
        {
            name: 'Role',
            selector: (row) => row.role || '--',
            sortable: true,
            width: '150px',
        },
        {
            name: 'Status',
            selector: (row) => row.status || 'Active',
            sortable: true,
        },
        {
            name: 'Options',
            cell: (row) => (
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {row.status === 'Active' ? (
                        <>
                            <button
                                className="btn btn-default p-0"
                                onClick={() => handleEdit(row)}
                                title="Edit"
                                disabled={isLoading || isSubmitting}
                            >
                                <img src="./assets/images/edit.svg" alt="Edit" />
                            </button>
                            <button
                                className="btn btn-default p-0"
                                onClick={() => handleDelete(row)}
                                title="Delete"
                                disabled={isLoading || isSubmitting}
                            >
                                <img src="./assets/images/delete.svg" alt="Delete" />
                            </button>
                            <button
                                className="btn btn-default p-0"
                                onClick={() => handleReset(row)}
                                title="Reset Password"
                                disabled={isLoading || isSubmitting}
                            >
                                <ArrowClockwise size={16} />
                            </button>
                        </>
                    ) : (
                        <button
                            type="submit"
                            className="btn btn-search mb-1"
                            style={{ width: '130px', margin: '5px 0 0 0' }}
                            onClick={() => handleActivate(row)}
                            disabled={isLoading || isSubmitting}
                        >
                            Re-activate
                        </button>
                    )}
                </div>
            ),
            ignoreRowClick: true,
        },
    ];

    const handlePageChange = (page) => {
        setPageNo(page);
        setSelectedRows([]);
    };

    const handlePerRowsChange = (newPerPage, page) => {
        setPerPage(newPerPage);
        setPageNo(page);
        setSelectedRows([]);
    };

    const handleTabClick = (tab) => {
        setActiveTab(tab);
        setPageNo(1);
        setPerPage(5);
        setSelectedRows([]);
        setUsers([]);
        setTotalRows(0);
    };

    useEffect(() => {
        if (pageNo && perPage && selectedCountry !== null) {
            fetchUsers();
        }
    }, [pageNo, perPage, activeTab, selectedRole]);

    const handleRowSelected = ({ selectedRows }) => {
        setSelectedRows(selectedRows);
    };

    const customStyles = {
        table: {
            style: {
                border: '1px solid #dee2e6',
            },
        },
        headRow: {
            style: {
                backgroundColor: '#2e7d32',
                color: '#ffffff',
                fontWeight: 'bold',
                borderBottom: '1px solid #dee2e6',
            },
        },
        headCells: {
            style: {
                padding: '10px 15px!important',
                borderRight: '1px solid #dee2e6',
            },
        },
        cells: {
            style: {
                padding: '5px 15px',
                borderRight: '1px solid #dee2e6',
            },
        },
        rows: {
            style: {
                maxHeight: '40px',
                '&:hover': {
                    backgroundColor: '#f1f1f1',
                },
            },
        },
        pagination: {
            style: {
                padding: '4px 8px',
                minHeight: '40px',
                border: 'none',
                padding: '5px 5px 5px 5px',
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                backgroundColor: '#e8f5e9',
            },
            pageButtonsStyle: {
                backgroundColor: 'transparent',
                border: 'none',
                color: '#000',
                padding: '0.25rem 0.5rem',
                margin: '0 0.25rem',
                cursor: 'pointer',
                '&:hover': {
                    backgroundColor: '#d4edda',
                },
                '&:disabled': {
                    opacity: 0.5,
                    cursor: 'not-allowed',
                },
            },
        },
    };

    return (
        <>
            <div className="wrapper pt-100px one">
                <div className="container-fluid">
                    <div className="row pt-3">
                        <div className="col-sm-12 col-md-12 col-lg-12">
                            <div className="box-config">
                                <h5>User Management</h5>
                                {isSuperAdmin && (
                                    <div className="row mb-3">
                                        <div className="col-sm-12 col-md-5 col-lg-5">
                                            <div className="row">
                                                <div className="col-sm-12 col-md-6 col-lg-6">
                                                    <div className="form-group me-2">
                                                        <label htmlFor="country">Country <span className="text-danger">*</span></label>
                                                        <select
                                                            className="form-select custom-select"
                                                            value={selectedCountry || ''}
                                                            onChange={onCountryChange}
                                                        >
                                                            <option value="">All</option>
                                                            {countries &&
                                                                countries.map((country) => (
                                                                    <option key={country.country_id} value={country.country_id}>
                                                                        {country.country || country.name}
                                                                    </option>
                                                                ))}
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className="col-sm-12 col-md-6 col-lg-6">
                                                    <div className="form-group me-2">
                                                        <label htmlFor="role">Role</label>
                                                        <select
                                                            className="form-select custom-select"
                                                            value={selectedRole || ''}
                                                            onChange={onRoleChange}
                                                        >
                                                            <option value="">All</option>
                                                            {roles.map((role) => (
                                                                <option key={role.role_id} value={role.role_id}>
                                                                    {role.role}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-sm-12 col-md-12 col-lg">
                            <div className="filter-box border p-2" style={{ borderRight: '0px', height: 'auto' }}>
                                <h5 className="text-capitalize">Users</h5>
                                <div className="d-flex bg-light">
                                    <div
                                        className={`tab ${activeTab === 'active' ? 'active-tab' : ''}`}
                                        onClick={() => handleTabClick('active')}
                                        style={{ cursor: 'pointer', padding: '10px 20px', fontSize: '14px', fontWeight: '500', borderBottom: activeTab === 'active' ? '2px solid #2e7d32' : 'none' }}
                                    >
                                        Active
                                    </div>
                                    <div
                                        className={`tab ${activeTab === 'deleted' ? 'active-tab' : ''}`}
                                        onClick={() => handleTabClick('deleted')}
                                        style={{ cursor: 'pointer', padding: '10px 20px', fontSize: '14px', fontWeight: '500', borderBottom: activeTab === 'deleted' ? '2px solid #2e7d32' : 'none' }}
                                    >
                                        Deleted
                                    </div>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <div>
                                        {selectedRows.length > 0 && (
                                            <p className="mb-0">
                                                {selectedRows.length} row(s) selected
                                            </p>
                                        )}
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <form onSubmit={handleSearchSubmit} className="d-flex align-items-center">
                                            <div className="searchBox me-2">
                                                <input
                                                    type="text"
                                                    placeholder="Search by name or email or phone"
                                                    className="form-control"
                                                    value={searchTerm}
                                                    onChange={(e) => {
                                                        setSearchTerm(e.target.value);
                                                    }}
                                                    disabled={isLoading || isSubmitting}
                                                />
                                                <span><img src="./assets/images/search.svg" alt="" /></span>
                                            </div>
                                            <button
                                                type="submit"
                                                className="btn btn-search"
                                                disabled={isLoading || isSubmitting}
                                            >
                                                Search
                                            </button>
                                        </form>
                                        <div>
                                            <button className="btn btn-new ms-2 pe-2" onClick={reset} style={{ marginBottom: '23px' }} disabled={isLoading || isSubmitting}>
                                                Reset
                                            </button>
                                        </div>
                                        <div>
                                            <button className="btn btn-new ms-2 pe-2" onClick={handleShow} style={{ marginBottom: '23px' }} disabled={isLoading || isSubmitting}>
                                                <span><img src="./assets/images/add.svg" alt="" style={{ height: '21px' }} /></span>Add New
                                            </button>
                                        </div>
                                        {/* New Export Button */}
                                        <div>
                                            <button
                                                className="btn btn-new ms-2 pe-2"
                                                onClick={handleExport}
                                                style={{ marginBottom: '23px' }}
                                                disabled={isLoading || isSubmitting}
                                            >
                                                <span><img src="./assets/images/export.svg" alt="" style={{ height: '21px' }} /></span>Export Data
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <DataTable
                                    key={activeTab}
                                    columns={columns}
                                    data={users}
                                    // selectableRows
                                    // onSelectedRowsChange={handleRowSelected}
                                    pagination
                                    paginationServer
                                    paginationTotalRows={totalRows}
                                    paginationDefaultPage={pageNo}
                                    onChangePage={handlePageChange}
                                    onChangeRowsPerPage={handlePerRowsChange}
                                    paginationRowsPerPageOptions={[5, 10, 25, 50]}
                                    paginationPerPage={perPage}
                                    paginationComponentOptions={{
                                        rowsPerPageText: 'Records per page:',
                                        rangeSeparatorText: 'of',
                                        selectAllRowsItem: false,
                                    }}
                                    customStyles={customStyles}
                                    noDataComponent={<div className="text-center p-2 font-14px">No Users found</div>}
                                    highlightOnHover
                                    dense
                                    keyField="user_id"
                                    progressPending={isLoading}
                                    progressComponent={<div className="text-center p-2 font-14px">Loading...</div>}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>

            <Offcanvas show={show} onHide={handleClose} placement="end" {...props}>
                <Offcanvas.Body>
                    <div className="add-new-box">
                        <h5>{isEditMode ? 'Edit User' : 'New User'}</h5>
                        <form onSubmit={handleOffcanvasSubmit}>
                            <div className="form-group">
                                <label htmlFor="user_type">User Type <span className="text-danger">*</span></label>
                                <select
                                    className="form-select custom-select"
                                    id="user_type"
                                    value={roleId}
                                    onChange={(e) => setRoleId(e.target.value)}
                                    required
                                    disabled={isSubmitting}
                                >
                                    <option value="">Select</option>
                                    {roles.filter(r => isSuperAdmin || r.role_id !== 1).map((role) => (
                                        <option key={role.role_id} value={role.role_id}>
                                            {role.role}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {+roleId >= 2 && (
                                <div className="form-group">
                                    <label htmlFor="country">Country <span className="text-danger">*</span></label>
                                    <select
                                        className="form-select custom-select"
                                        id="country"
                                        value={offcanvasCountry}
                                        onChange={(e) => setOffcanvasCountry(e.target.value)}
                                        required
                                        disabled={!isSuperAdmin || isSubmitting}
                                    >
                                        <option value="">Select</option>
                                        {countries &&
                                            countries.map((country) => (
                                                <option key={country.country_id} value={country.country_id}>
                                                    {country.country || country.name}
                                                </option>
                                            ))}
                                    </select>
                                </div>
                            )}
                            {+roleId >= 3 && (
                                <div className="form-group">
                                    <label htmlFor="province">Province <span className="text-danger">*</span></label>
                                    <select
                                        className="form-select custom-select"
                                        id="province"
                                        value={offcanvasProvince}
                                        onChange={(e) => setOffcanvasProvince(e.target.value)}
                                        disabled={!offcanvasCountry || isSubmitting}
                                        required
                                    >
                                        <option value="">Select</option>
                                        {provinces.map((province) => (
                                            <option key={province.adm1_id} value={province.adm1_id}>
                                                {province.adm1}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {+roleId >= 4 && (
                                <div className="form-group">
                                    <label htmlFor="district">District <span className="text-danger">*</span></label>
                                    <select
                                        className="form-select custom-select"
                                        id="district"
                                        value={offcanvasDistrict}
                                        onChange={(e) => setOffcanvasDistrict(e.target.value)}
                                        disabled={!offcanvasProvince || isSubmitting}
                                        required
                                    >
                                        <option value="">Select</option>
                                        {districts.map((district) => (
                                            <option key={district.adm2_id} value={district.adm2_id}>
                                                {district.adm2}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {+roleId >= 5 && (
                                <div className="form-group">
                                    <label htmlFor="village">{offcanvasCountry == 1 ? 'EPA' : 'Camp'} <span className="text-danger">*</span></label>
                                    <select
                                        className="form-select custom-select"
                                        id="village"
                                        value={offcanvasVillage}
                                        onChange={(e) => setOffcanvasVillage(e.target.value)}
                                        disabled={!offcanvasDistrict || isSubmitting}
                                        required
                                    >
                                        <option value="">Select</option>
                                        {villages.map((village) => (
                                            <option key={village.adm3_id} value={village.adm3_id}>
                                                {village.adm3}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div className="form-group">
                                <label htmlFor="first_name">First Name <span className="text-danger">*</span></label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="first_name"
                                    placeholder="Enter"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    required
                                    disabled={isSubmitting}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="last_name">Last Name <span className="text-danger">*</span></label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="last_name"
                                    placeholder="Enter"
                                    value={lastName || ''}
                                    onChange={(e) => setLastName(e.target.value)}
                                    required
                                    disabled={isSubmitting}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="email">Email <span className="text-danger">*</span></label>
                                <input
                                    type="email"
                                    className="form-control"
                                    id="email"
                                    placeholder="Enter a valid email address"
                                    value={email}
                                    onChange={handleEmailChange}
                                    maxLength="100"
                                    required
                                    pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
                                    title="Please enter a valid email address (e.g., user@example.com)."
                                    disabled={isEditMode || isSubmitting}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="phone">Phone <span className="text-danger">*</span></label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="phone"
                                    placeholder="Enter phone number with country code"
                                    value={phone}
                                    onChange={handlePhoneChange}
                                    maxLength="16"
                                    required
                                    pattern="^\d{1,16}$"
                                    title="Phone number must contain only digits, up to 16 characters."
                                    disabled={isSubmitting}
                                />
                            </div>
                            {offcanvasFormError && <div className="text-danger">{offcanvasFormError}</div>}
                            <div className="row pt-5 mt-150px">
                                <div className="col-sm-12 col-md-6 col-lg-6">
                                    <button type="button" className="btn btn-cancel w-100" onClick={handleClose} disabled={isSubmitting}>
                                        Cancel
                                    </button>
                                </div>
                                <div className="col-sm-12 col-md-6 col-lg-6">
                                    <button type="submit" className="btn btn-submit w-100" disabled={isSubmitting}>
                                        {isEditMode ? 'Update' : 'Save'}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
}

export default UserManagement;